import { Injectable, Inject, DOCUMENT } from '@angular/core';

import { ClientTheme } from '../models/client.model';

/**
 * Applies the venue's dynamic menu theme to CSS custom properties on
 * `document.documentElement`. Ported from the iOS `AppTheme` layer: each of the
 * 7 admin-configurable menu colors falls back to today's hardcoded default when
 * the API sends nothing, and a set of luminance-aware derived vars flip between
 * light/dark tints so a custom light background stays legible.
 *
 * SCSS references these via `var(--menu-*, <fallback>)`; this service only sets
 * the runtime overrides. The `:root` defaults in styles.scss stand whenever a
 * field is missing or malformed.
 */
@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  /** Fallback values — must match the `:root` defaults in styles.scss. */
  private readonly defaults = {
    background: '#191919',
    itemBackground: '#252525',
    text: '#FFFFFF',
    description: '#CFCFCF',
    price: '#FF6064',
    accent: '#FF6064',
    accentText: '#FFFFFF',
    imageBorder: '#2B2B2B',
  };

  constructor(@Inject(DOCUMENT) private document: Document) {}

  /** Read clientData from sessionStorage and apply. Safe no-op when absent. */
  applyThemeFromStorage(): void {
    const raw = sessionStorage.getItem('clientData');
    if (!raw) {
      return;
    }
    try {
      this.applyTheme(JSON.parse(raw));
    } catch (e) {
      console.error('ThemeService: failed to parse clientData', e);
    }
  }

  /** Resolve the 7 fields (with fallbacks), set base + derived CSS vars. */
  applyTheme(clientData: (ClientTheme & Record<string, any>) | null | undefined): void {
    const c = clientData ?? {};

    const background = c.menu_background_color || this.defaults.background;
    const itemBackground = c.menu_item_background_color || this.defaults.itemBackground;
    const text = c.menu_text_color || this.defaults.text;
    const description = c.menu_description_color || this.defaults.description;
    const price = c.menu_price_color || this.defaults.price;
    const accent = c.menu_accent_color || this.defaults.accent;
    const accentText = c.menu_accent_text_color || this.defaults.accentText;
    const imageBorder = c.menu_image_border_color || this.defaults.imageBorder;

    // Base colors (+ rgb triplet for rgba(var(--x-rgb), a) opacity variants).
    this.setColor('--menu-background', background);
    this.setColor('--menu-item-background', itemBackground);
    this.setColor('--menu-text', text);
    this.setColor('--menu-description', description);
    this.setColor('--menu-price', price);
    this.setColor('--menu-accent', accent);
    this.setColor('--menu-accent-text', accentText);
    this.setColor('--menu-image-border', imageBorder);

    // Luminance-derived vars (port of iOS AppTheme). Background brightness
    // decides whether elevated overlays tint white (dark bg) or black (light bg).
    const bgRgb = this.hexToRgb(background);
    const isLight = bgRgb
      ? (0.299 * bgRgb.r + 0.587 * bgRgb.g + 0.114 * bgRgb.b) / 255 > 0.5
      : false;
    const tint = isLight ? '0, 0, 0' : '255, 255, 255';

    this.setVar('--menu-elevated-fill', `rgba(${tint}, 0.08)`);
    this.setVar('--menu-elevated-stroke', `rgba(${tint}, 0.12)`);
    this.setVar('--menu-item-border', `rgba(${tint}, ${isLight ? 0.12 : 0.1})`);
    this.setVar('--menu-shadow', isLight ? 'rgba(0, 0, 0, 0.18)' : 'rgba(0, 0, 0, 0.42)');
    this.setVar('--menu-shimmer-tint', `rgba(${tint}, ${isLight ? 0.12 : 0.2})`);
    this.setVar('--menu-on-surface', isLight ? '#000000' : text);
    // The triplet behind it, so a gradient can vary its own alpha across stops — the
    // promo card's hairline needs two opacities of the same tint.
    this.setVar('--menu-on-surface-rgb', tint);

    /*
     * Success green, for the applied-promo checkmark.
     *
     * iOS uses system `.green`, which is already luminance-adaptive. #2ecc71 was
     * hardcoded on web and reads washed out against a pale background, so it darkens
     * on a light venue. It is deliberately NOT derived from the accent: a venue whose
     * accent is red must still be able to say "this worked" in green.
     */
    this.setVar('--menu-success', isLight ? '#27A35B' : '#2ECC71');
  }

  /** Set a color var plus its `-rgb` triplet. Skips malformed hex (default holds). */
  private setColor(name: string, value: string): void {
    const rgb = this.hexToRgb(value);
    if (!rgb) {
      return;
    }
    this.setVar(name, value);
    this.setVar(`${name}-rgb`, `${rgb.r}, ${rgb.g}, ${rgb.b}`);
  }

  private setVar(name: string, value: string): void {
    this.document.documentElement.style.setProperty(name, value);
  }

  /** Parse #RGB / #RRGGBB / #RRGGBBAA. Returns null on anything else. */
  private hexToRgb(hex: string): { r: number; g: number; b: number } | null {
    if (!hex) {
      return null;
    }
    let h = hex.trim().replace(/^#/, '');
    if (h.length === 3) {
      h = h
        .split('')
        .map((ch) => ch + ch)
        .join('');
    }
    if (h.length !== 6 && h.length !== 8) {
      return null;
    }
    if (!/^[0-9a-fA-F]+$/.test(h)) {
      return null;
    }
    const int = parseInt(h.slice(0, 6), 16);
    return {
      r: (int >> 16) & 0xff,
      g: (int >> 8) & 0xff,
      b: int & 0xff,
    };
  }
}
